"use client";

import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { previewDepositAmount } from "@/lib/deposit-preview";

export type ServiceRow = {
  id: string;
  name: string;
  description: string | null;
  duration: number;
  price: number | null;
  requiresDeposit: boolean;
  depositAmount: number | null;
  depositType: string | null;
  depositCurrency: string | null;
  isActive: boolean;
  isVirtual: boolean;
  programOnly: boolean;
};

const label = css({ fontSize: "xs", color: "#64748b" });

function parsePriceInput(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : Number.NaN;
}

/** Modal único para crear y editar servicios (mismo componente, distinto modo según `service`). */
export function ServiceFormModal({
  open,
  onClose,
  service,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** null = modo creación; con datos = modo edición, precargado. */
  service: ServiceRow | null;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const isEdit = Boolean(service);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("30");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [requiresDeposit, setRequiresDeposit] = useState(false);
  const [depositType, setDepositType] = useState<"fixed" | "percentage">("fixed");
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [depositCurrency, setDepositCurrency] = useState("ARS");
  const [isVirtual, setIsVirtual] = useState(false);
  const [programOnly, setProgramOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setName(service?.name ?? "");
    setDuration(String(service?.duration ?? 30));
    setPrice(service?.price != null ? String(service.price) : "");
    setDescription(service?.description ?? "");
    setRequiresDeposit(service?.requiresDeposit ?? false);
    setDepositType(service?.depositType === "percentage" ? "percentage" : "fixed");
    setDepositAmount(service?.depositAmount ?? 0);
    setDepositCurrency(service?.depositCurrency ?? "ARS");
    setIsVirtual(service?.isVirtual ?? false);
    setProgramOnly(service?.programOnly ?? false);
  }, [open, service]);

  const preview = previewDepositAmount({
    requiresDeposit,
    depositType,
    depositAmount,
    price: price.trim() === "" ? null : Number(price),
  });

  async function submit() {
    setErr(null);
    const nameTrim = name.trim();
    if (nameTrim.length < 2) {
      setErr("El nombre debe tener al menos 2 caracteres.");
      return;
    }
    const d = Number.parseInt(duration, 10);
    if (!Number.isFinite(d) || d < 5 || d > 24 * 60) {
      setErr("Duración inválida (entre 5 minutos y 24 h).");
      return;
    }
    const p = parsePriceInput(price);
    if (price.trim() !== "" && (p == null || Number.isNaN(p) || p < 0)) {
      setErr("Precio inválido.");
      return;
    }
    if (requiresDeposit && (!depositAmount || depositAmount <= 0)) {
      setErr("Ingresá un monto o porcentaje de seña mayor a 0.");
      return;
    }

    setBusy(true);
    try {
      if (isEdit && service) {
        const r = await fetchWithSessionSync(
          `/api/dashboard/services/${service.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: nameTrim,
              duration: d,
              price: p,
              description: description.trim() || null,
              isVirtual,
              programOnly,
              requiresDeposit,
              depositType,
              depositAmount: requiresDeposit ? depositAmount : null,
              depositCurrency: requiresDeposit ? depositCurrency.trim() || "ARS" : null,
            }),
          },
          refresh,
        );
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { error?: string };
          setErr(j.error ?? "No se pudo guardar.");
          return;
        }
      } else {
        const r = await fetchWithSessionSync(
          "/api/dashboard/services",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: nameTrim,
              duration: d,
              price: p,
              description: description.trim() || null,
              isVirtual,
              programOnly,
            }),
          },
          refresh,
        );
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { error?: string };
          setErr(j.error ?? "No se pudo crear el servicio.");
          return;
        }
        if (requiresDeposit) {
          const created = (await r.json()) as { id: string };
          await fetchWithSessionSync(
            `/api/dashboard/services/${created.id}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                requiresDeposit: true,
                depositType,
                depositAmount,
                depositCurrency: depositCurrency.trim() || "ARS",
              }),
            },
            refresh,
          );
        }
      }
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Editar servicio" : "Nuevo servicio"} size="md">
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        {err ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Nombre</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Consulta médica" />
        </label>

        <div className={css({ display: "grid", gap: "3", gridTemplateColumns: "1fr 1fr" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Duración (minutos)</span>
            <Input type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Precio (opcional)</span>
            <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Vacío si no aplica" />
          </label>
        </div>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Descripción (opcional)</span>
          <textarea
            className={css({
              width: "100%",
              px: "3",
              py: "2",
              rounded: "md",
              borderWidth: "1px",
              borderColor: "border.DEFAULT",
              bg: "bg.DEFAULT",
              color: "fg.DEFAULT",
              fontSize: "sm",
              minH: "20",
              resize: "vertical",
              _focus: { outline: "none", borderColor: "accent.DEFAULT", boxShadow: "0 0 0 1px token(colors.brand.500)" },
            })}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div
          className={css({
            pt: "3",
            borderTopWidth: "1px",
            borderColor: "#e2e8f0",
            display: "flex",
            flexDir: "column",
            gap: "3",
          })}
        >
          <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", color: "#334155" })}>
            <input type="checkbox" checked={requiresDeposit} onChange={(e) => setRequiresDeposit(e.target.checked)} />
            Requerir seña para este servicio
          </label>
          {requiresDeposit ? (
            <>
              <div className={css({ display: "flex", flexWrap: "wrap", gap: "4", fontSize: "sm" })}>
                <label className={css({ display: "flex", alignItems: "center", gap: "2" })}>
                  <input
                    type="radio"
                    name="deposit-type"
                    checked={depositType === "fixed"}
                    onChange={() => setDepositType("fixed")}
                  />
                  Monto fijo ({depositCurrency})
                </label>
                <label className={css({ display: "flex", alignItems: "center", gap: "2" })}>
                  <input
                    type="radio"
                    name="deposit-type"
                    checked={depositType === "percentage"}
                    onChange={() => setDepositType("percentage")}
                  />
                  Porcentaje del precio total
                </label>
              </div>
              <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", alignItems: "end" })}>
                <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                  <span className={label}>{depositType === "percentage" ? "Porcentaje (%)" : "Monto"}</span>
                  <Input
                    type="number"
                    min={0}
                    step={depositType === "percentage" ? 1 : 0.01}
                    value={Number.isNaN(depositAmount) ? "" : depositAmount}
                    onChange={(e) => setDepositAmount(Number(e.target.value))}
                  />
                </label>
                {depositType === "fixed" ? (
                  <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                    <span className={label}>Moneda (MP)</span>
                    <Input
                      value={depositCurrency}
                      onChange={(e) => setDepositCurrency(e.target.value.toUpperCase())}
                      maxLength={8}
                    />
                  </label>
                ) : null}
              </div>
              <p className={css({ fontSize: "sm", color: "#475569" })}>
                Vista previa: el cliente deberá pagar{" "}
                <strong>{preview != null ? `${depositCurrency === "ARS" ? "$" : `${depositCurrency} `}${preview}` : "—"}</strong>{" "}
                al reservar.
              </p>
            </>
          ) : null}
        </div>

        <label
          className={css({
            display: "flex",
            alignItems: "center",
            gap: "2",
            fontSize: "sm",
            color: "#334155",
            pt: "3",
            borderTopWidth: "1px",
            borderColor: "#e2e8f0",
          })}
        >
          <input type="checkbox" checked={isVirtual} onChange={(e) => setIsVirtual(e.target.checked)} />
          ¿Es una consulta virtual? (Se enviará link de Google Meet al confirmar)
        </label>

        <label className={css({ display: "flex", alignItems: "flex-start", gap: "2", fontSize: "sm", color: "#334155" })}>
          <input
            type="checkbox"
            checked={programOnly}
            onChange={(e) => setProgramOnly(e.target.checked)}
            className={css({ mt: "0.5" })}
          />
          <span>
            Solo disponible para pacientes con programa activo
            <span className={css({ display: "block", fontSize: "xs", color: "#94a3b8", mt: "0.5" })}>
              No aparece en la agenda pública general; solo lo ven pacientes con un programa de tratamiento que lo incluya.
            </span>
          </span>
        </label>

        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end", pt: "2" })}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>
            {busy ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear servicio"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
